import {
  parseCalendarDate,
  todayIso,
  getCurrentFiscalYear,
  fiscalYearFromIso,
} from "./dates";

/**
 * Billing year for annual dues based on the firm's billing month (First Dues
 * Date) rather than the WSG fiscal year: charging rolls over to the next year
 * the month AFTER the billing month. E.g. a March billing month charges the
 * current year through March, then the next year starting in April. Firms
 * without a billing start date fall back to a July billing month, which
 * reproduces the legacy WSG August rollover.
 */
export function billingYearForDate(
  referenceIso: string,
  billingStartDate?: string,
): number {
  const reference =
    parseCalendarDate(referenceIso) ?? parseCalendarDate(todayIso());
  if (!reference) {
    return new Date().getFullYear();
  }
  const referenceYear = reference.getUTCFullYear();
  const referenceMonth = reference.getUTCMonth() + 1;
  const anniversary = parseCalendarDate(billingStartDate);
  const billingMonth = anniversary ? anniversary.getUTCMonth() + 1 : 7;
  return referenceMonth > billingMonth ? referenceYear + 1 : referenceYear;
}

/** WSG fiscal year stored on membership-dues invoices for an office processing date. */
export function membershipDuesFiscalYearForOffice(
  processingDateIso: string,
  billingStartDate?: string,
): number {
  const billingYear = billingYearForDate(processingDateIso, billingStartDate);
  return fiscalYearFromIso(
    billingPeriodStartIso(billingYear, billingStartDate),
  );
}

/** Start date (billing month/day) of the billing period within a billing year. */
export function billingPeriodStartIso(
  billingYear: number,
  billingStartDate?: string,
): string {
  const anniversary = parseCalendarDate(billingStartDate);
  const month = anniversary ? anniversary.getUTCMonth() + 1 : 7;
  const day = anniversary ? anniversary.getUTCDate() : 1;
  return `${billingYear}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export const SAVED_INVOICE_TIER_PREFIX = "__saved__:";

export function savedInvoiceTierRangeId(amount: number): string {
  return `${SAVED_INVOICE_TIER_PREFIX}${amount}`;
}

export function isSavedInvoiceTierRange(range: string): boolean {
  return range.startsWith(SAVED_INVOICE_TIER_PREFIX);
}

export function feeFromSavedInvoiceTierRange(range: string): number | null {
  if (!isSavedInvoiceTierRange(range)) {
    return null;
  }
  const amount = Number(range.slice(SAVED_INVOICE_TIER_PREFIX.length));
  return Number.isFinite(amount) ? amount : null;
}

/** Catalog tier key for initiation/other per-range fees (not a synthetic saved tier id). */
export function resolveCatalogTierRangeForFees(
  tierRange: string,
  duesTiers: { range: string; fee: number }[],
  savedAnnualAmount?: number,
): string {
  if (!isSavedInvoiceTierRange(tierRange)) {
    return tierRange;
  }
  const matched = duesTiers.find((tier) => tier.fee === savedAnnualAmount);
  return matched?.range ?? duesTiers[0]?.range ?? "";
}

/** Start year of the current WSG billing period (e.g. 2026 for period 2026-2027). */
export function getCurrentBillingPeriodStartYear(): number {
  return getCurrentFiscalYear() - 1;
}

/** Default accounting fiscal-year filter: present WSG billing period. */
export function getDefaultAccountingBillingPeriodStartYear(
  currentFiscalYear = getCurrentFiscalYear(),
): number {
  return currentFiscalYear - 1;
}

/** Display label for a WSG billing period (Aug startYear – Jul startYear+1). */
export function billingPeriodRangeLabel(startYear: number): string {
  return `${startYear}-${startYear + 1}`;
}

/** WSG fiscal year / fee catalog year from billing period start year. */
export function fiscalYearFromBillingPeriodStartYear(startYear: number): number {
  return startYear + 1;
}
